import React, { useState } from 'react';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import {
  UploadCloud,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileText,
  Sparkles,
  ArrowRight,
  Check,
} from 'lucide-react';
import { SocietyHierarchyData, societiesService } from '../../../api/societiesService';
import { importsService } from '../../../api/importsService';
import { useToast } from '../../../hooks/useToast';
import { extractErrorMessage } from '../../../utils/errorExtractor';

interface SocietyImportTabProps {
  data: SocietyHierarchyData;
  onRefresh: () => void;
}

interface ParsedRowPreview {
  block: string;
  floor: number;
  flat: string;
  ownerName: string;
  phone: string;
  email: string;
  status: 'valid' | 'error' | 'warning';
  message?: string;
}

export const SocietyImportTab: React.FC<SocietyImportTabProps> = ({
  data,
  onRefresh,
}) => {
  const toast = useToast();
  const { society, blocks } = data;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCommitted, setIsCommitted] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchStats, setBatchStats] = useState<{ total: number; valid: number; invalid: number } | null>(null);
  const [previewRows, setPreviewRows] = useState<ParsedRowPreview[]>([]);

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloading(true);
      const blob = await societiesService.downloadPreFilledTemplate(society.id, society.name);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${society.name.replace(/\s+/g, '_')}_Owner_Import_Template.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Pre-filled society template downloaded successfully.');
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to download template'));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = async (file: File) => {
    setSelectedFile(file);
    setIsCommitted(false);
    setBatchId(null);
    setBatchStats(null);
    setPreviewRows([]);

    try {
      setIsValidating(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('society_id', society.id);

      const res = await importsService.uploadExcel(formData);
      if (res.success && res.data) {
        const batch = res.data;
        setBatchId(batch.id);
        setBatchStats({
          total: batch.total_rows || 0,
          valid: batch.valid_rows || 0,
          invalid: batch.invalid_rows || 0,
        });

        // Fetch staged preview rows from backend
        const previewRes = await importsService.previewRows(batch.id, { limit: 100 });
        const items = (previewRes as any).data?.rows || (previewRes as any).rows || previewRes.data || [];
        
        if (Array.isArray(items) && items.length > 0) {
          const mapped: ParsedRowPreview[] = items.map((r: any) => {
            const raw = r.raw_data || {};
            const norm = r.normalized_data || {};
            return {
              block: raw.block_name || norm.block_name || 'Block A',
              floor: raw.floor_number ?? norm.floor_number ?? 1,
              flat: raw.flat_number || norm.flat_number || raw.bungalow_number || '',
              ownerName: raw.resident_name || norm.resident_name || (raw.relationship_to_owner === 'Vacant' ? '(Vacant)' : '-'),
              phone: raw.resident_phone || norm.resident_phone || '-',
              email: raw.resident_email || norm.resident_email || '-',
              status: r.validation_status === 'valid' ? 'valid' : 'error',
              message: r.error_messages ? r.error_messages.join(', ') : undefined,
            };
          });
          setPreviewRows(mapped);
        }

        if ((batch.invalid_rows || 0) > 0) {
          toast.warning(`File uploaded: ${batch.valid_rows} valid rows, ${batch.invalid_rows} rows with validation errors.`);
        } else {
          toast.success(`File validated successfully: ${batch.valid_rows} rows ready for import.`);
        }
      } else {
        toast.error(res.message || 'Failed to parse file');
      }
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to upload and validate file'));
    } finally {
      setIsValidating(false);
    }
  };

  const handleCommitImport = async () => {
    if (!batchId) {
      toast.error('No validated import batch found to commit');
      return;
    }

    try {
      setIsUploading(true);
      const res = await importsService.commit(batchId);

      if (res.success) {
        setIsCommitted(true);
        toast.success('Owner and flat records successfully imported and committed!');
        onRefresh();
      } else {
        toast.error(res.message || 'Import commit failed');
      }
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Import commit failed'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Top Template Download Card */}
      <div className="p-5 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-indigo-500/10 border border-emerald-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-sm shrink-0">
            <FileSpreadsheet className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-900">
              Download Pre-Filled Template for {society.name}
            </h4>
            <p className="text-xs text-slate-600 mt-0.5">
              Includes all configured blocks ({blocks.map((b) => b.name).join(', ') || 'Blocks'}) and flat numbers ready for filling.
            </p>
          </div>
        </div>

        <Button
          variant="primary"
          size="sm"
          onClick={handleDownloadTemplate}
          disabled={isDownloading}
          leftIcon={<Download className="w-4 h-4" />}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {isDownloading ? 'Generating...' : 'Download Pre-Filled Template (.CSV)'}
        </Button>
      </div>

      {/* Upload Zone Card */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <UploadCloud className="w-5 h-5 text-indigo-600" />
            <span className="font-bold text-slate-900">Bulk Import Resident & Owner Database</span>
          </div>
        }
        subtitle="Upload your completed Excel (.xlsx) or CSV template file to automatically populate flats and owners."
      >
        <div className="space-y-4">
          {/* Drag & Drop File Zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
            className={`p-8 border-2 border-dashed rounded-2xl text-center transition-all ${
              selectedFile
                ? 'border-emerald-300 bg-emerald-50/20'
                : 'border-slate-300 hover:border-indigo-400 bg-slate-50/50 hover:bg-indigo-50/20'
            }`}
          >
            <input
              type="file"
              id="file-upload"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileInputChange}
              className="hidden"
            />

            <div className="flex flex-col items-center">
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 shadow-inner ${
                  selectedFile ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                }`}
              >
                {selectedFile ? <FileSpreadsheet className="w-7 h-7" /> : <UploadCloud className="w-7 h-7" />}
              </div>

              {selectedFile ? (
                <div>
                  <h4 className="text-sm font-bold text-slate-900">{selectedFile.name}</h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {(selectedFile.size / 1024).toFixed(1)} KB &bull; {isValidating ? 'Validating spreadsheet rows...' : batchStats ? `${batchStats.valid} valid of ${batchStats.total} rows ready` : 'Ready for validation & commit'}
                  </p>
                  <label
                    htmlFor="file-upload"
                    className="inline-block mt-3 text-xs font-bold text-indigo-600 hover:text-indigo-700 cursor-pointer"
                  >
                    Choose a different file
                  </label>
                </div>
              ) : (
                <div>
                  <h4 className="text-sm font-bold text-slate-900">
                    Drag and drop your completed Excel/CSV file here
                  </h4>
                  <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                    Supports .xlsx, .xls, and .csv files formatted according to the pre-filled template.
                  </p>
                  <label
                    htmlFor="file-upload"
                    className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-xl bg-white border border-slate-300 hover:border-indigo-400 text-slate-700 font-bold text-xs shadow-xs cursor-pointer transition-all hover:bg-slate-50"
                  >
                    <UploadCloud className="w-3.5 h-3.5 text-indigo-600" />
                    Browse Files
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Validation & Preview Grid */}
          {selectedFile && (previewRows.length > 0 || isValidating) && (
            <div className="space-y-4 pt-4 border-t border-slate-100 animate-fadeIn">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Data Validation & Preview ({previewRows.length} Rows Parsed)
                  </h4>
                </div>
                {isValidating ? (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 animate-pulse">
                    Validating Rows...
                  </span>
                ) : batchStats && batchStats.invalid > 0 ? (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                    {batchStats.valid} Valid / {batchStats.invalid} Invalid Rows
                  </span>
                ) : (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    All {previewRows.length} Rows Validated
                  </span>
                )}
              </div>

              {previewRows.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-slate-200 max-h-96">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider sticky top-0 z-10 shadow-2xs">
                      <tr>
                        <th className="py-2.5 px-3">Status</th>
                        <th className="py-2.5 px-3">Block</th>
                        <th className="py-2.5 px-3">Floor</th>
                        <th className="py-2.5 px-3">Flat No</th>
                        <th className="py-2.5 px-3">Owner Full Name</th>
                        <th className="py-2.5 px-3">Phone</th>
                        <th className="py-2.5 px-3">Email</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-medium">
                      {previewRows.map((row, idx) => (
                        <tr key={idx} className={row.status === 'valid' ? 'hover:bg-slate-50/50' : 'bg-rose-50/30 hover:bg-rose-50/50'}>
                          <td className="py-2.5 px-3">
                            {row.status === 'valid' ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                <Check className="w-3 h-3" />
                                Valid
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200" title={row.message}>
                                <XCircle className="w-3 h-3" />
                                Error
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 font-bold text-slate-900">{row.block}</td>
                          <td className="py-2.5 px-3 text-slate-600">F{row.floor}</td>
                          <td className="py-2.5 px-3 font-bold text-indigo-600">{row.flat}</td>
                          <td className="py-2.5 px-3 text-slate-800">{row.ownerName}</td>
                          <td className="py-2.5 px-3 text-slate-600">{row.phone}</td>
                          <td className="py-2.5 px-3 text-slate-500">{row.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Commit Action Bar */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>
                    Ready to commit: {batchStats ? batchStats.valid : previewRows.length} valid records will be linked to their units in {society.name}.
                  </span>
                </div>

                <Button
                  variant="primary"
                  onClick={handleCommitImport}
                  disabled={isValidating || isUploading || isCommitted || !batchId || (batchStats !== null && batchStats.valid === 0)}
                  leftIcon={
                    isCommitted ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <UploadCloud className="w-4 h-4" />
                    )
                  }
                  className={isCommitted ? 'bg-emerald-600 text-white' : ''}
                >
                  {isCommitted
                    ? 'Import Completed!'
                    : isUploading
                    ? 'Importing Database...'
                    : isValidating
                    ? 'Validating File...'
                    : 'Commit & Import Data'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
